import { FC, MouseEvent, ReactNode, useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X } from 'phosphor-react';
import { Conditional } from '../Common/Conditional';

interface ModalProps {
  showModal: boolean;
  setShowModal?: (value: boolean) => void;
  header?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  content?: ReactNode;
  bottomStaticInfo?: ReactNode;
  small?: boolean;
  outsideClickCloses?: boolean;
}

export const Modal: FC<ModalProps> = ({
  showModal,
  setShowModal,
  header,
  description,
  actions,
  content,
  bottomStaticInfo,
  small,
  outsideClickCloses = true,
}) => {
  const [maxMiddleHeight, setMaxMiddleHeight] = useState(window.innerHeight);

  const determineMiddleModalHeight = () => {
    const PADDING_Y = 20;

    const topModal = document.getElementById('top-modal');
    const middleModal = document.getElementById('middle-modal');
    const bottomModal = document.getElementById('bottom-modal');

    if (!topModal || !middleModal || !bottomModal) {
      return;
    }

    const newMaxMiddleHeight = window.innerHeight - topModal.clientHeight - bottomModal.clientHeight - 2 * PADDING_Y;
    setMaxMiddleHeight(newMaxMiddleHeight);
  };

  useEffect(() => {
    window.addEventListener('resize', determineMiddleModalHeight);
    determineMiddleModalHeight();

    if (showModal) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('resize', determineMiddleModalHeight);
      document.body.style.overflow = 'unset';
    };
  }, [showModal]);

  const escFunction = useCallback(
    (e: KeyboardEvent) => {
      if (setShowModal && outsideClickCloses && e.key === 'Escape') {
        setShowModal(false);
      }
    },
    [outsideClickCloses, setShowModal],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  const closeModal = (e?: MouseEvent) => {
    if (!outsideClickCloses) {
      return;
    }

    e?.stopPropagation();
    if (setShowModal) {
      setShowModal(false);
    }
  };

  const getBorders = () => {
    return `border-input-border sm:border-transparent`;
  };

  return (
    <Conditional
      condition={showModal}
      trueRender={
        <div
          onClick={closeModal}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black cursor-default backdrop-blur-sm bg-opacity-40"
        >
          <motion.div
            initial={{ y: '150%' }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.4,
            }}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            className={`relative ${
              small ? 'w-11/12 sm:w-[600px]' : 'w-11/12 mx-4 sm:w-2/3 sm:max-w-[800px]'
            } rounded-2xl`}
          >
            <div className="flex flex-col w-full bg-white shadow-xl outline-none focus:outline-none rounded-2xl">
              <div
                id="top-modal"
                className={`flex flex-row items-start pt-6 pb-4 px-modal-side-mobile sm:px-modal-side-desktop sm:pt-10 sm:pb-2 border-b border-input-border sm:border-b-0
                `}
              >
                <div className="text-xl font-bold leading-tightsm:w-4/5 text-text-dark sm:text-2xl">{header}</div>
                <button onClick={closeModal} className="ml-auto text-text-dark">
                  <Conditional condition={outsideClickCloses} trueRender={<X size={32} />} />
                </button>
              </div>

              <div
                id="middle-modal"
                style={{ maxHeight: maxMiddleHeight }}
                className={`flex flex-col px-modal-side-mobile sm:px-modal-side-desktop pt-2 pb-4 ${getBorders()}`}
              >
                <div className="flex flex-col overflow-y-auto">
                  <Conditional
                    condition={description !== undefined}
                    trueRender={
                      <div className="pt-2 pb-6 text-base font-medium sm:pt-0 sm:pb-10 text-text-dark">
                        {description}
                      </div>
                    }
                    falseRender={<div className="pb-2 sm:pb-4" />}
                  />

                  <Conditional
                    condition={content !== undefined}
                    trueRender={<div className="pb-0 sm:pb-4">{content}</div>}
                  />
                </div>

                <Conditional condition={bottomStaticInfo !== undefined} trueRender={<div>{bottomStaticInfo}</div>} />
              </div>

              <div
                id="bottom-modal"
                className="pt-4 pb-3 border-t px-modal-side-mobile sm:px-modal-side-desktop sm:pb-10 border-input-border sm:border-t-0"
              >
                {actions}
              </div>
            </div>
          </motion.div>
        </div>
      }
    />
  );
};
